import { getSession, signOut, useSession } from 'next-auth/react';
import { useCallback } from 'react';

import backendService from '../services/backendService';
import { isNewAuthProvider } from '../utils/newAuthProviderIds';
import { clearPostAuthRedirect } from '../utils/postAuthRedirect';

async function attemptClearCache() {
  try {
    await backendService.instance.users.clearCache();
  } catch (error) {}
}

function useSignOut() {
  const { data: session } = useSession();

  // Legacy Azure B2C: clear the NextAuth session and bounce through B2C's
  // hosted sign-out so the IdP session is cleared too.
  const b2cSignout = useCallback(async (redirectUrl?: string) => {
    const url = await backendService.instance.getSignOutUrl();
    if (url) {
      await attemptClearCache(); // Even if this fails, user should be signed out
      clearPostAuthRedirect();
      await signOut({
        callbackUrl: `${url}?post_logout_redirect_uri=${redirectUrl ||
          window.location.origin}`,
      });
    }
  }, []);

  // New passwordless / direct-OAuth providers (CURI-334): there's no hosted IdP
  // sign-out to round-trip through — just clear the Curipod session and land back
  // on our own origin.
  const newAuthSignout = useCallback(async (redirectUrl?: string) => {
    await attemptClearCache();
    clearPostAuthRedirect();
    await signOut({ callbackUrl: redirectUrl || window.location.origin });
  }, []);

  // Sign out using the flow that matches however the user authenticated.
  const signOutAuto = useCallback(
    async (redirectUrl?: string) => {
      // useSession() may still be `loading` on an immediate click, leaving
      // `provider` undefined — which would misroute new-auth users to the legacy
      // B2C sign-out. Fall back to a fresh getSession() so the flow is correct.
      let provider = session?.provider;
      if (!provider) {
        const fresh = await getSession();
        provider = fresh?.provider;
      }
      if (isNewAuthProvider(provider)) {
        await newAuthSignout(redirectUrl);
      } else {
        await b2cSignout(redirectUrl);
      }
    },
    [session?.provider, b2cSignout, newAuthSignout],
  );

  return {
    b2cSignout,
    newAuthSignout,
    signOutAuto,
  };
}

export default useSignOut;
